/**
 * EnforceOrganization Component
 *
 * Ensures that authenticated users have an organization membership.
 * If user has no organization, redirects to organization selection/creation flow.
 * Enforces organization-scoped routing for all protected routes.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import { useAuth } from "@clerk/nextjs";
import { useConvexAuth, useMutation, useQuery } from "convex/react";
import type { Route } from "next";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { SubscriptionRequiredDialog } from "@/components/billing/subscription-required-dialog";
import { PageWrapperLoading } from "@/components/page-wrapper";
import { buildOrganizationPath, getRelativeOrganizationPath, isPathWithinOrganization } from "@/lib/organization-path";
import { capturePosthogEvent } from "@/lib/posthog";

// Thrown errors (e.g. the auth context still settling right after org creation) can be
// transient, so retry a bounded number of times with exponential backoff before giving up.
// Deterministic `success: false` results are never retried.
const MAX_FREE_SUBSCRIPTION_ATTEMPTS = 3;
const FREE_SUBSCRIPTION_RETRY_BASE_DELAY_MS = 500;
interface EnforceOrganizationProps {
  children: ReactNode;
}
export function EnforceOrganization(t0) {
  const $ = _c(76);
  const {
    children
  } = t0;
  const {
    isLoaded: isClerkLoaded,
    isSignedIn
  } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const organizationStatus = useQuery(api.check_membership.hasOrganization);
  const ensureFreeSubscription = useMutation(api.payments.mutations.ensureFreeSubscriptionForActiveOrganization);
  const [retryVersion, setRetryVersion] = useState(0);
  const [freeSubscriptionFailedSlug, setFreeSubscriptionFailedSlug] = useState(null);
  const freeSubscriptionAttemptSlugRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      slug: null,
      attempts: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const freeSubscriptionRetryRef = useRef(t1);
  const freeSubscriptionRetryTimerRef = useRef(null);
  let t2;
  if ($[1] !== pathname) {
    t2 = pathname.startsWith("/onboarding");
    $[1] = pathname;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isOnboardingRoute = t2;
  const isAppRoute = pathname === "/app";
  const isAuthenticated = organizationStatus?.isAuthenticated ?? false;
  const isAuthBootstrapping = isClerkLoaded && isSignedIn && !isAuthenticated;
  const isLoading = !isClerkLoaded || organizationStatus === undefined || isAuthBootstrapping;
  const hasOrganization = organizationStatus?.hasOrganization ?? false;
  const activeOrganizationSlug = organizationStatus?.activeOrganizationSlug ?? null;
  const billingStatus = organizationStatus?.billingStatus ?? "none";
  const hasActiveSubscription = organizationStatus?.hasActiveSubscription ?? false;
  let t3;
  if ($[3] !== activeOrganizationSlug) {
    t3 = activeOrganizationSlug !== null ? buildOrganizationPath(activeOrganizationSlug, "/") : null;
    $[3] = activeOrganizationSlug;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const organizationDashboardPath = t3;
  const isOrganizationDashboardRoute = organizationDashboardPath !== null && pathname === organizationDashboardPath;
  const isBillingRequiredRoute = activeOrganizationSlug !== null && pathname === `/${activeOrganizationSlug}/billing-required`;
  const isBillingSettingsRoute = activeOrganizationSlug !== null && pathname === `/${activeOrganizationSlug}/settings/billing`;
  const isUnpaidAllowedRoute = isOrganizationDashboardRoute || isBillingSettingsRoute;
  const isWaitingForFreeSubscription = isSignedIn && hasOrganization && activeOrganizationSlug !== null && !hasActiveSubscription && billingStatus === "none" && freeSubscriptionFailedSlug !== activeOrganizationSlug;
  let t4;
  if ($[5] !== activeOrganizationSlug || $[6] !== billingStatus || $[7] !== ensureFreeSubscription || $[8] !== freeSubscriptionFailedSlug || $[9] !== hasActiveSubscription || $[10] !== hasOrganization || $[11] !== isLoading || $[12] !== isSignedIn) {
    t4 = () => {
      if (isLoading || !isSignedIn || !hasOrganization || !activeOrganizationSlug || hasActiveSubscription || billingStatus !== "none" || freeSubscriptionAttemptSlugRef.current === activeOrganizationSlug || freeSubscriptionFailedSlug === activeOrganizationSlug) {
        return;
      }
      if (freeSubscriptionRetryTimerRef.current) {
        clearTimeout(freeSubscriptionRetryTimerRef.current);
        freeSubscriptionRetryTimerRef.current = null;
      }
      if (freeSubscriptionRetryRef.current.slug !== activeOrganizationSlug) {
        freeSubscriptionRetryRef.current = {
          slug: activeOrganizationSlug,
          attempts: 0
        };
      }
      freeSubscriptionAttemptSlugRef.current = activeOrganizationSlug;
      ensureFreeSubscription({}).then(result => {
        if (!result.success) {
          setFreeSubscriptionFailedSlug(activeOrganizationSlug);
        }
      }).catch(error => {
        console.error("Failed to ensure free subscription:", error);
        const attempts = freeSubscriptionRetryRef.current.attempts + 1;
        freeSubscriptionRetryRef.current = {
          slug: activeOrganizationSlug,
          attempts
        };
        if (attempts >= MAX_FREE_SUBSCRIPTION_ATTEMPTS) {
          setFreeSubscriptionFailedSlug(activeOrganizationSlug);
          return;
        }
        const delay = FREE_SUBSCRIPTION_RETRY_BASE_DELAY_MS * 2 ** (attempts - 1);
        freeSubscriptionRetryTimerRef.current = setTimeout(() => {
          freeSubscriptionAttemptSlugRef.current = null;
          setRetryVersion(_temp);
        }, delay);
      }).finally(() => {
        setRetryVersion(_temp2);
      });
    };
    $[5] = activeOrganizationSlug;
    $[6] = billingStatus;
    $[7] = ensureFreeSubscription;
    $[8] = freeSubscriptionFailedSlug;
    $[9] = hasActiveSubscription;
    $[10] = hasOrganization;
    $[11] = isLoading;
    $[12] = isSignedIn;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== activeOrganizationSlug || $[15] !== billingStatus || $[16] !== ensureFreeSubscription || $[17] !== freeSubscriptionFailedSlug || $[18] !== hasActiveSubscription || $[19] !== hasOrganization || $[20] !== isLoading || $[21] !== isSignedIn || $[22] !== retryVersion) {
    t5 = [isLoading, isSignedIn, hasOrganization, activeOrganizationSlug, hasActiveSubscription, billingStatus, freeSubscriptionFailedSlug, ensureFreeSubscription, retryVersion];
    $[14] = activeOrganizationSlug;
    $[15] = billingStatus;
    $[16] = ensureFreeSubscription;
    $[17] = freeSubscriptionFailedSlug;
    $[18] = hasActiveSubscription;
    $[19] = hasOrganization;
    $[20] = isLoading;
    $[21] = isSignedIn;
    $[22] = retryVersion;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  useEffect(t4, t5);
  const {
    isAuthenticated: isConvexAuthenticated
  } = useConvexAuth();
  const hasBeenReadyRef = useRef(false);
  const lostAtRef = useRef(null);
  let t6;
  let t7;
  if ($[24] !== isClerkLoaded || $[25] !== isConvexAuthenticated || $[26] !== isLoading || $[27] !== isSignedIn || $[28] !== organizationStatus) {
    t6 = () => {
      if (!isLoading) {
        if (lostAtRef.current !== null) {
          capturePosthogEvent("AgroProj:Auth:PageRestored", {
            durationMs: Date.now() - lostAtRef.current
          });
          lostAtRef.current = null;
        }
        hasBeenReadyRef.current = true;
        return;
      }
      if (!hasBeenReadyRef.current || lostAtRef.current !== null) {
        return;
      }
      lostAtRef.current = Date.now();
      capturePosthogEvent("AgroProj:Auth:PageLost", {
        reason: !isClerkLoaded ? "clerk_loading" : organizationStatus === undefined ? "status_loading" : "convex_unauthenticated",
        clerkSignedIn: Boolean(isSignedIn),
        convexAuthenticated: isConvexAuthenticated,
        visibility: document.visibilityState,
        online: navigator.onLine
      });
    };
    t7 = [isLoading, isClerkLoaded, isSignedIn, isConvexAuthenticated, organizationStatus];
    $[24] = isClerkLoaded;
    $[25] = isConvexAuthenticated;
    $[26] = isLoading;
    $[27] = isSignedIn;
    $[28] = organizationStatus;
    $[29] = t6;
    $[30] = t7;
  } else {
    t6 = $[29];
    t7 = $[30];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => () => {
      if (freeSubscriptionRetryTimerRef.current) {
        clearTimeout(freeSubscriptionRetryTimerRef.current);
      }
    };
    t9 = [];
    $[31] = t8;
    $[32] = t9;
  } else {
    t8 = $[31];
    t9 = $[32];
  }
  useEffect(t8, t9);
  let t10;
  if ($[33] !== activeOrganizationSlug || $[34] !== hasActiveSubscription || $[35] !== hasOrganization || $[36] !== isAppRoute || $[37] !== isBillingRequiredRoute || $[38] !== isLoading || $[39] !== isOnboardingRoute || $[40] !== isSignedIn || $[41] !== isUnpaidAllowedRoute || $[42] !== isWaitingForFreeSubscription || $[43] !== pathname || $[44] !== router || $[45] !== searchParams) {
    t10 = () => {
      if (isLoading || isWaitingForFreeSubscription) {
        return;
      }
      if (!isSignedIn) {
        router.replace("/sign-in");
        return;
      }
      if (!hasOrganization || !activeOrganizationSlug) {
        if (!isOnboardingRoute && !isAppRoute) {
          router.replace("/onboarding/choose-organization");
        }
        return;
      }
      if (!hasActiveSubscription && isBillingRequiredRoute) {
        router.replace(buildOrganizationPath(activeOrganizationSlug, "/") as Route);
        return;
      }
      if (!hasActiveSubscription && !isUnpaidAllowedRoute) {
        router.replace(buildOrganizationPath(activeOrganizationSlug, "/") as Route);
        return;
      }
      if (hasActiveSubscription && isBillingRequiredRoute) {
        router.replace(buildOrganizationPath(activeOrganizationSlug, "/") as Route);
        return;
      }
      if (isOnboardingRoute && pathname === "/onboarding/choose-organization") {
        return;
      }
      if (isOnboardingRoute) {
        const returnTo = searchParams.get("returnTo");
        const target = returnTo ? buildOrganizationPath(activeOrganizationSlug, returnTo) : buildOrganizationPath(activeOrganizationSlug, "/");
        router.replace(target as Route);
        return;
      }
      const isWithinOrg = isPathWithinOrganization(activeOrganizationSlug, pathname);
      if (!isWithinOrg) {
        const redirectPath = buildOrganizationPath(activeOrganizationSlug, getRelativeOrganizationPath(activeOrganizationSlug, pathname));
        router.replace(redirectPath as Route);
      }
    };
    $[33] = activeOrganizationSlug;
    $[34] = hasActiveSubscription;
    $[35] = hasOrganization;
    $[36] = isAppRoute;
    $[37] = isBillingRequiredRoute;
    $[38] = isLoading;
    $[39] = isOnboardingRoute;
    $[40] = isSignedIn;
    $[41] = isUnpaidAllowedRoute;
    $[42] = isWaitingForFreeSubscription;
    $[43] = pathname;
    $[44] = router;
    $[45] = searchParams;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  let t11;
  if ($[47] !== activeOrganizationSlug || $[48] !== hasActiveSubscription || $[49] !== hasOrganization || $[50] !== isAppRoute || $[51] !== isAuthenticated || $[52] !== isBillingRequiredRoute || $[53] !== isLoading || $[54] !== isOnboardingRoute || $[55] !== isSignedIn || $[56] !== isUnpaidAllowedRoute || $[57] !== isWaitingForFreeSubscription || $[58] !== pathname || $[59] !== router || $[60] !== searchParams) {
    t11 = [isLoading, isAuthenticated, isSignedIn, hasOrganization, activeOrganizationSlug, hasActiveSubscription, isWaitingForFreeSubscription, isBillingRequiredRoute, isUnpaidAllowedRoute, isOnboardingRoute, isAppRoute, pathname, router, searchParams];
    $[47] = activeOrganizationSlug;
    $[48] = hasActiveSubscription;
    $[49] = hasOrganization;
    $[50] = isAppRoute;
    $[51] = isAuthenticated;
    $[52] = isBillingRequiredRoute;
    $[53] = isLoading;
    $[54] = isOnboardingRoute;
    $[55] = isSignedIn;
    $[56] = isUnpaidAllowedRoute;
    $[57] = isWaitingForFreeSubscription;
    $[58] = pathname;
    $[59] = router;
    $[60] = searchParams;
    $[61] = t11;
  } else {
    t11 = $[61];
  }
  useEffect(t10, t11);
  if (isLoading) {
    let t12;
    if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <PageWrapperLoading />;
      $[62] = t12;
    } else {
      t12 = $[62];
    }
    return t12;
  }
  if (isWaitingForFreeSubscription) {
    let t12;
    if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <PageWrapperLoading />;
      $[63] = t12;
    } else {
      t12 = $[63];
    }
    return t12;
  }
  if (!isSignedIn) {
    let t12;
    if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <PageWrapperLoading />;
      $[64] = t12;
    } else {
      t12 = $[64];
    }
    return t12;
  }
  if (!hasOrganization || !activeOrganizationSlug) {
    if (isOnboardingRoute || isAppRoute) {
      return children;
    }
    let t12;
    if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <PageWrapperLoading />;
      $[65] = t12;
    } else {
      t12 = $[65];
    }
    return t12;
  }
  if (isOnboardingRoute && pathname === "/onboarding/choose-organization") {
    return children;
  }
  if (isOnboardingRoute) {
    let t12;
    if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <PageWrapperLoading />;
      $[66] = t12;
    } else {
      t12 = $[66];
    }
    return t12;
  }
  if (!hasActiveSubscription && isBillingRequiredRoute) {
    let t12;
    if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <PageWrapperLoading />;
      $[67] = t12;
    } else {
      t12 = $[67];
    }
    return t12;
  }
  if (!hasActiveSubscription && !isUnpaidAllowedRoute) {
    let t12;
    if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <PageWrapperLoading />;
      $[68] = t12;
    } else {
      t12 = $[68];
    }
    return t12;
  }
  const isWithinOrg_0 = isPathWithinOrganization(activeOrganizationSlug, pathname);
  if (!isWithinOrg_0) {
    let t12;
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <PageWrapperLoading />;
      $[69] = t12;
    } else {
      t12 = $[69];
    }
    return t12;
  }
  let t12;
  if ($[70] !== hasActiveSubscription || $[71] !== isOrganizationDashboardRoute) {
    t12 = !hasActiveSubscription && isOrganizationDashboardRoute ? <SubscriptionRequiredDialog /> : null;
    $[70] = hasActiveSubscription;
    $[71] = isOrganizationDashboardRoute;
    $[72] = t12;
  } else {
    t12 = $[72];
  }
  let t13;
  if ($[73] !== children || $[74] !== t12) {
    t13 = <>{children}{t12}</>;
    $[73] = children;
    $[74] = t12;
    $[75] = t13;
  } else {
    t13 = $[75];
  }
  return t13;
}
function _temp2(version_0) {
  return version_0 + 1;
}
function _temp(version) {
  return version + 1;
}